import { BookOpen, Brain, Check, ChevronDown, Loader2, Wrench } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { PhaseStep, ResponsePhase } from "../types";

function StepRow({ step }: { step: PhaseStep }) {
  const [showResult, setShowResult] = useState(false);
  const hasResult = step.isComplete && step.result;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => hasResult && setShowResult((prev) => !prev)}
        className={`flex items-center gap-2 px-3 py-1 text-xs text-muted-foreground/70 ${hasResult ? "cursor-pointer hover:bg-muted rounded" : "cursor-default"}`}
      >
        {step.isComplete ? (
          <Check className="size-3 shrink-0 text-muted-foreground/50" />
        ) : (
          <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground/50" />
        )}
        <span className="truncate">{step.name}</span>
        {hasResult && (
          <ChevronDown className={`ml-auto size-3 shrink-0 text-muted-foreground/40 transition-transform ${showResult ? "rotate-180" : ""}`} />
        )}
      </button>
      {showResult && step.result && (
        <div className="mx-3 mb-1 overflow-hidden rounded-lg border border-border px-3 py-2">
          <pre className="max-h-40 overflow-auto text-xs leading-relaxed text-muted-foreground">
            <code>{formatResult(step.result)}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

function formatResult(result: string): string {
  try {
    return JSON.stringify(JSON.parse(result), null, 2);
  } catch {
    return result;
  }
}

const staggerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0 },
};

const UNDERSTAND_FALLBACK = ["Understanding your question..."];
const FETCH_FALLBACK = ["Fetching data..."];

function useRotatingMessage(messages: string[], active: boolean, intervalMs = 3000): string {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, intervalMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [active, messages.length, intervalMs]);

  return messages[index]!;
}

type PhaseIndicatorProps = {
  phase: ResponsePhase;
  thinkingContent?: string;
  thinkingDurationSeconds?: number;
  isThinkingActive?: boolean;
};

export function PhaseIndicator({
  phase,
  thinkingContent,
  thinkingDurationSeconds,
  isThinkingActive,
}: PhaseIndicatorProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const isActive = phase.status === "active";
  const completedSteps = phase.steps.filter((s) => s.isComplete).length;
  const totalSteps = phase.steps.length;
  const currentStep = phase.steps.find((s) => !s.isComplete);

  const hasThinking = phase.id === "understand" && !!thinkingContent;
  const hasExpandableContent = totalSteps > 0 || hasThinking;

  const Icon = phase.id === "understand" ? BookOpen : Wrench;

  const idleAndActive = isActive && !currentStep;
  const understandMessages = (t("captain.phase.understanding", { returnObjects: true, defaultValue: UNDERSTAND_FALLBACK }) as string[] | string);
  const fetchMessages = (t("captain.phase.fetching", { returnObjects: true, defaultValue: FETCH_FALLBACK }) as string[] | string);
  const understandArr = Array.isArray(understandMessages) ? understandMessages : UNDERSTAND_FALLBACK;
  const fetchArr = Array.isArray(fetchMessages) ? fetchMessages : FETCH_FALLBACK;

  const rotatingUnderstand = useRotatingMessage(understandArr, idleAndActive && phase.id === "understand");
  const rotatingFetch = useRotatingMessage(fetchArr, idleAndActive && phase.id === "fetch");

  const label = isActive
    ? currentStep?.name ?? (phase.id === "understand" ? rotatingUnderstand : rotatingFetch)
    : phase.id === "understand"
      ? t("captain.phase.understood")
      : t("captain.phase.fetchedData", { count: totalSteps });

  return (
    <motion.div layout transition={{ type: "spring", stiffness: 400, damping: 30 }} className="flex flex-col">
      <button
        type="button"
        onClick={() => hasExpandableContent && setExpanded((prev) => !prev)}
        className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors ${hasExpandableContent ? "cursor-pointer hover:bg-muted" : "cursor-default"}`}
      >
        {isActive ? (
          <Loader2 className="size-4 shrink-0 animate-spin" />
        ) : (
          <Icon className="size-4 shrink-0" />
        )}

        <span className="truncate">{label}</span>

        {!isActive && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 15 }}
          >
            <Check className="size-3.5 shrink-0 text-muted-foreground/60" />
          </motion.span>
        )}

        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {isActive && totalSteps > 1 && (
            <span className="text-xs text-muted-foreground/50">
              {completedSteps}/{totalSteps}
            </span>
          )}
          {hasExpandableContent && (
            <ChevronDown
              className={`size-3.5 text-muted-foreground/50 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          )}
        </span>
      </button>

      {/* Progress bar for active phase */}
      {isActive && totalSteps > 0 && (
        <div className="mx-3 mt-0.5 h-px overflow-hidden rounded-full bg-muted">
          <motion.div
            className="h-full rounded-full bg-muted-foreground/20"
            initial={{ width: "0%" }}
            animate={{ width: `${(completedSteps / totalSteps) * 100}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>
      )}

      {/* Expandable content with spring animation + staggered children */}
      <AnimatePresence>
        {expanded && hasExpandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="overflow-hidden"
          >
            <motion.div
              className="ml-6 flex flex-col gap-px py-1"
              initial="hidden"
              animate="visible"
              variants={staggerVariants}
            >
              {/* Reasoning content (inside understand phase) */}
              {hasThinking && (
                <motion.div variants={itemVariants} className="flex items-start gap-2 px-3 py-1">
                  <Brain className="mt-0.5 size-3 shrink-0 text-muted-foreground/50" />
                  <div className="min-w-0">
                    {thinkingDurationSeconds != null && (
                      <span className="text-xs text-muted-foreground/50">
                        {isThinkingActive
                          ? t("captain.phase.reasoning")
                          : t("captain.phase.reasonedFor", { seconds: thinkingDurationSeconds })}
                      </span>
                    )}
                    <p className="mt-0.5 text-xs italic text-muted-foreground/60 line-clamp-4">
                      {thinkingContent}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Tool call steps */}
              {phase.steps.map((step, i) => (
                <motion.div key={step.callId ?? i} variants={itemVariants}>
                  <StepRow step={step} />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
